import React, { useState, useMemo } from 'react';
import styled, { useTheme } from 'styled-components';
import { tokens } from '../../utils/theme';

const Container = styled.div`
    background: ${props => props.theme.palette.mode === 'dark'
        ? 'radial-gradient(circle at top left, rgba(130, 170, 255, 0.05), rgba(12, 14, 23, 0.95))'
        : 'radial-gradient(circle at top left, rgba(15,118,110,0.04), rgba(255,255,255,0.95))'};
    border: 1px solid ${props => tokens(props.theme.palette.mode).grey[500]};
    border-radius: 14px;
    padding: 20px 20px 0 20px;
    color: ${props => props.theme.palette.text.primary};
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
`;

const ScrollableContent = styled.div`
    flex: 1;
    overflow-y: auto;
    padding-right: 10px;
    padding-bottom: 24px;
    margin-right: -10px;

    &::-webkit-scrollbar {
        width: 6px;
        height: 6px;
    }
    &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.22);
        border-radius: 999px;
        border: 1px solid rgba(255, 255, 255, 0.08);
    }
    &::-webkit-scrollbar-thumb {
        background: linear-gradient(180deg, rgba(128, 203, 196, 0.26), rgba(128, 203, 196, 0.44));
        border-radius: 999px;
        border: 1px solid rgba(128, 203, 196, 0.20);
    }
    &::-webkit-scrollbar-thumb:hover {
        background: linear-gradient(180deg, rgba(128, 203, 196, 0.4), rgba(128, 203, 196, 0.6));
    }
`;

const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const SummaryStrip = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;

    @media (max-width: 900px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const StatCard = styled.div`
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.22)' : 'rgba(255, 255, 255, 0.6)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.06)'};
    border-radius: 8px;
    padding: 10px 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const StatLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${props => tokens(props.theme.palette.mode).grey[400]};
`;

const StatValue = styled.span`
    font-size: 16px;
    font-weight: 700;
    color: ${props => props.$color || (props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220')};
    font-variant-numeric: tabular-nums;
`;

const TableCard = styled.div`
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.22)' : 'rgba(255, 255, 255, 0.6)'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(15, 23, 42, 0.06)'};
    border-radius: 8px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const TableHeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
`;

const Title = styled.h3`
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'};
`;

const SearchInput = styled.input`
    background: ${props => props.theme.palette.mode === 'dark' ? '#0d1117' : '#fff'};
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(15, 23, 42, 0.15)'};
    border-radius: 6px;
    color: ${props => props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220'};
    font-size: 12px;
    padding: 8px 14px;
    height: 36px;
    box-sizing: border-box;
    width: 240px;
    outline: none;
    transition: all 0.2s ease;

    &:focus {
        border-color: #80cbc4;
        box-shadow: 0 0 0 2px rgba(128, 203, 196, 0.15);
    }

    &::placeholder {
        color: ${props => tokens(props.theme.palette.mode).grey[400]};
        font-size: 11.5px;
    }
`;

const TableWrapper = styled.div`
    overflow-x: auto;
    border-radius: 6px;
    border: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'};
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
`;

const Th = styled.th`
    background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(15, 23, 42, 0.03)'};
    color: ${props => tokens(props.theme.palette.mode).grey[300]};
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    text-align: ${props => props.$align || 'left'};
    padding: 9px 12px;
    border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'};
    white-space: nowrap;
    cursor: ${props => props.$sortable ? 'pointer' : 'default'};
    user-select: none;

    &:hover {
        color: ${props => props.$sortable ? '#80cbc4' : undefined};
    }
`;

const Td = styled.td`
    padding: 8px 12px;
    border-bottom: 1px solid ${props => props.theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(15, 23, 42, 0.04)'};
    text-align: ${props => props.$align || 'left'};
    color: ${props => props.$color || (props.theme.palette.mode === 'dark' ? '#e6edf7' : '#0b1220')};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

const Tr = styled.tr`
    cursor: pointer;
    transition: background 0.12s ease;

    &:hover {
        background: ${props => props.theme.palette.mode === 'dark' ? 'rgba(128, 203, 196, 0.08)' : 'rgba(15, 118, 110, 0.05)'};
    }
`;

const CodeBadge = styled.span`
    display: inline-block;
    padding: 1px 6px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 700;
    background: rgba(128, 203, 196, 0.14);
    color: #80cbc4;
    border: 1px solid rgba(128, 203, 196, 0.25);
    margin-right: 8px;
`;

export default function GlobalIndicatorsTab({ data, onSelectCountry }) {
    const theme = useTheme();
    const [search, setSearch] = useState('');
    const [sortKey, setSortKey] = useState('gdp');
    const [sortDir, setSortDir] = useState('desc');

    const countries = useMemo(() => data?.countries || [], [data?.countries]);
    const summary = data?.summary || {};

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    const filtered = useMemo(() => {
        let list = [...countries];
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(c =>
                c.name.toLowerCase().includes(q) ||
                c.code.toLowerCase().includes(q) ||
                c.centralBank.toLowerCase().includes(q)
            );
        }

        list.sort((a, b) => {
            let valA = a[sortKey];
            let valB = b[sortKey];
            if (typeof valA === 'string') {
                return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
            }
            return sortDir === 'asc' ? (valA - valB) : (valB - valA);
        });

        return list;
    }, [countries, search, sortKey, sortDir]);

    const sortIndicator = (key) => {
        if (sortKey !== key) return '';
        return sortDir === 'asc' ? ' ↑' : ' ↓';
    };

    return (
        <Container>
            <ScrollableContent>
                <Stack>
                    {/* Compact Macro Highlights */}
                    <SummaryStrip>
                        <StatCard>
                            <StatLabel>Covered GDP</StatLabel>
                            <StatValue>${summary.totalGdpCovered?.toLocaleString()} B</StatValue>
                        </StatCard>
                        <StatCard>
                            <StatLabel>Avg. Growth</StatLabel>
                            <StatValue $color={summary.averageGrowth >= 1 ? tokens(theme.palette.mode).greenAccent[500] : '#ffb74d'}>
                                +{summary.averageGrowth}%
                            </StatValue>
                        </StatCard>
                        <StatCard>
                            <StatLabel>Avg. Inflation</StatLabel>
                            <StatValue $color={summary.averageInflation > 3 ? tokens(theme.palette.mode).redAccent[500] : tokens(theme.palette.mode).greenAccent[500]}>
                                {summary.averageInflation}%
                            </StatValue>
                        </StatCard>
                        <StatCard>
                            <StatLabel>Avg. Policy Rate</StatLabel>
                            <StatValue $color="#80cbc4">
                                {summary.averagePolicyRate}%
                            </StatValue>
                        </StatCard>
                    </SummaryStrip>

                    {/* Table View */}
                    <TableCard>
                        <TableHeaderRow>
                            <Title>GLOBAL INDICATORS</Title>
                            <SearchInput
                                type="text"
                                placeholder="Search country or currency..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </TableHeaderRow>

                        <TableWrapper>
                            <Table>
                                <thead>
                                    <tr>
                                        <Th $sortable onClick={() => handleSort('name')}>COUNTRY{sortIndicator('name')}</Th>
                                        <Th $align="right" $sortable onClick={() => handleSort('gdp')}>GDP ($B){sortIndicator('gdp')}</Th>
                                        <Th $align="right" $sortable onClick={() => handleSort('gdpGrowth')}>GROWTH{sortIndicator('gdpGrowth')}</Th>
                                        <Th $align="right" $sortable onClick={() => handleSort('interestRate')}>INTEREST{sortIndicator('interestRate')}</Th>
                                        <Th $align="right" $sortable onClick={() => handleSort('inflationRate')}>INFLATION{sortIndicator('inflationRate')}</Th>
                                        <Th $align="right" $sortable onClick={() => handleSort('realInterestRate')}>REAL RATE{sortIndicator('realInterestRate')}</Th>
                                        <Th $align="right" $sortable onClick={() => handleSort('joblessRate')}>JOBLESS{sortIndicator('joblessRate')}</Th>
                                        <Th $align="right" $sortable onClick={() => handleSort('govBudget')}>BUDGET{sortIndicator('govBudget')}</Th>
                                        <Th $align="right" $sortable onClick={() => handleSort('debtToGdp')}>DEBT/GDP{sortIndicator('debtToGdp')}</Th>
                                        <Th $align="right" $sortable onClick={() => handleSort('currentAccount')}>CURR. ACC{sortIndicator('currentAccount')}</Th>
                                        <Th $align="right" $sortable onClick={() => handleSort('population')}>POP (M){sortIndicator('population')}</Th>
                                        <Th $align="center" $sortable onClick={() => handleSort('healthScore')}>HEALTH{sortIndicator('healthScore')}</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map(c => {
                                        const growthColor = c.gdpGrowth > 0
                                            ? tokens(theme.palette.mode).greenAccent[500]
                                            : (c.gdpGrowth < 0 ? tokens(theme.palette.mode).redAccent[500] : tokens(theme.palette.mode).grey[300]);

                                        const inflationColor = c.inflationRate > 4.0
                                            ? tokens(theme.palette.mode).redAccent[500]
                                            : (c.inflationRate <= 2.5 ? tokens(theme.palette.mode).greenAccent[500] : '#ffb74d');

                                        const budgetColor = c.govBudget >= 0 ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500];
                                        const debtColor = c.debtToGdp > 100 ? '#ef5350' : (c.debtToGdp > 60 ? '#ffb74d' : tokens(theme.palette.mode).greenAccent[500]);
                                        const realRateColor = c.realInterestRate > 0 ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500];

                                        return (
                                            <Tr key={c.id} onClick={() => onSelectCountry && onSelectCountry(c.id)}>
                                                <Td>
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <CodeBadge>{c.code}</CodeBadge>
                                                        <span style={{ fontWeight: 600 }}>{c.name}</span>
                                                    </div>
                                                </Td>
                                                <Td $align="right" style={{ fontWeight: 600 }}>
                                                    ${c.gdp?.toLocaleString()}
                                                </Td>
                                                <Td $align="right" $color={growthColor} style={{ fontWeight: 600 }}>
                                                    {c.gdpGrowth > 0 ? `+${c.gdpGrowth.toFixed(2)}%` : `${c.gdpGrowth.toFixed(2)}%`}
                                                </Td>
                                                <Td $align="right" style={{ fontWeight: 600, color: '#80cbc4' }}>
                                                    {c.interestRate.toFixed(2)}%
                                                </Td>
                                                <Td $align="right" $color={inflationColor} style={{ fontWeight: 600 }}>
                                                    {c.inflationRate.toFixed(2)}%
                                                </Td>
                                                <Td $align="right" $color={realRateColor} style={{ fontWeight: 600 }}>
                                                    {c.realInterestRate > 0 ? `+${c.realInterestRate.toFixed(2)}%` : `${c.realInterestRate.toFixed(2)}%`}
                                                </Td>
                                                <Td $align="right" $color={c.joblessRate > 6 ? '#ef5350' : (c.joblessRate < 4 ? tokens(theme.palette.mode).greenAccent[500] : undefined)}>
                                                    {c.joblessRate.toFixed(2)}%
                                                </Td>
                                                <Td $align="right" $color={budgetColor}>
                                                    {c.govBudget > 0 ? `+${c.govBudget.toFixed(2)}%` : `${c.govBudget.toFixed(2)}%`}
                                                </Td>
                                                <Td $align="right" $color={debtColor}>
                                                    {c.debtToGdp.toFixed(1)}%
                                                </Td>
                                                <Td $align="right" $color={c.currentAccount >= 0 ? tokens(theme.palette.mode).greenAccent[500] : tokens(theme.palette.mode).redAccent[500]}>
                                                    {c.currentAccount > 0 ? `+${c.currentAccount.toFixed(2)}%` : `${c.currentAccount.toFixed(2)}%`}
                                                </Td>
                                                <Td $align="right" $color={tokens(theme.palette.mode).grey[400]}>
                                                    {c.population?.toFixed(1)}
                                                </Td>
                                                <Td $align="center" style={{ fontWeight: 600, color: c.healthScore >= 70 ? '#80cbc4' : '#ffb74d' }}>
                                                    {c.healthScore !== undefined ? `${(c.healthScore / 10).toFixed(1)}/10` : '-'}
                                                </Td>
                                            </Tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </TableWrapper>
                    </TableCard>
                </Stack>
            </ScrollableContent>
        </Container>
    );
}
